import React, { useState } from 'react';
import { Button, Dropdown, Input, InputNumber, Modal, Popconfirm, Select, Spin, Tooltip, Upload } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import type { CharacterAppearance, FilmCharacterIdentityInput, FilmCharacter, FilmCharacterLook, FilmRefAsset, FilmScene, FilmSceneLook, ImageModelInfo } from '../../types/film';
import { APPEARANCE_LABELS, APPEARANCE_PLACEHOLDERS, appearancePayload, countFilledLabels, type AppearanceKey } from './characterAppearanceUtils';
import { characterCardCountsOf, characterCardFoldOf, genderChip, newLookHint, pickSelectedLook } from './characterCardUtils';
import CharacterIdentityField from './CharacterIdentityField';
import { identityLineOf, staleChipLabel } from './characterIdentityUtils';
import { faceNotes, faceSourceChip, faceThumbOf, sheetModelOptions, staleRegenPlan } from './faceSourceUtils';
import { formatOrders, sceneRuns, type SceneRun } from './sceneStripUtils';
import { lookImageLayout, lookImageState } from './bankPrepUtils';
import { avisChip, refAssetView } from './refAssetUtils';
import { useBankVars } from './useBankVars';
import WfIconButton from './WfIconButton'; // UI-assettabs
import { IconChip } from './StatusIcon';
import { lookLabelOf } from './assetCardsUi';
import type { IconName } from './workflowStatusIcons';
import './bank.css';
import './assetCards.css';

/**
 * Thumbnail bộ đồ: ảnh là sheet NGANG 16:9 = 1 ô cận ~40% + 3 ô góc (261008 E1-P2; trùng bố cục sheet 4 khung 260922) hoặc ảnh 2 khung 4:3 cũ (260930) ⇒ gắn class `sheet4` / `sheet2`
 * (theo tỉ lệ ảnh — `lookImageLayout`) để CSS lấy ô cận mặt (giữ 40% / 55% bên trái) thay vì giữa ảnh (ô toàn thân). Ảnh dọc cũ giữ nguyên. React không ghi `className` cho các <img> này nên class
 * gắn tay không bị ghi đè; đổi `src` thì `onLoad` chạy lại.
 */
const markSheet = (e: React.SyntheticEvent<HTMLImageElement>) => {
  const i = e.currentTarget;
  const layout = lookImageLayout(i.naturalWidth, i.naturalHeight);
  i.classList.remove('sheet', 'sheet4', 'sheet2'); // 'sheet' = class cũ, gỡ cho sạch nếu còn
  if (layout !== 'single') i.classList.add(layout);
};

/**
 * Thẻ nhân vật mới (đợt 260922 E2-P1) — dựng theo mockup `260922-mockup-bank.html` User duyệt 23/09.
 *
 * Từ trên xuống: khung ảnh 16:9 (MỘT ảnh — bộ đồ đang chọn; mặt ghim là thumbnail góc) → tab bộ đồ → mô tả bộ đang chọn → hàng nút →
 * tên/vai → 7 nhãn ngoại hình (sửa tại chỗ) → giọng.
 *
 * 🔴 Hai mô tả trang phục KHÁC NHAU, cố ý không gộp một dòng như mockup:
 *   · `Clothing & props` (trong 7 nhãn) = bộ MẶC ĐỊNH, thuộc hồ sơ nhân vật (`film_characters.appearance`);
 *   · "Bộ đang chọn" = `film_character_looks.outfit` — THỨ ĐƯỢC DÙNG ĐỂ GEN ẢNH bộ đồ.
 * Sửa cái trước không lan sang cái sau (đã gặp 260814) ⇒ gộp một dòng là người sửa xong bấm gen mà ảnh không đổi.
 */
export interface FilmCharacterCardProps {
  character: FilmCharacter;
  /** Bộ đồ CỦA nhân vật này, đã xếp `default` đầu (`looksOf`). */
  looks: FilmCharacterLook[];
  isSeedance: boolean;
  /** Việc đang chạy trên nhân vật (`voice` | `delete` | …) — khoá nút tương ứng. */
  busyAction?: string;
  /** id bộ đồ đang gen ảnh. */
  genLookIds: Set<string>;
  onSaveAppearance: (c: FilmCharacter, appearance: CharacterAppearance | null) => Promise<void>;
  onSaveField: (c: FilmCharacter, field: 'name' | 'role', value: string) => Promise<void>;
  onSaveGender: (c: FilmCharacter, gender: string | null) => Promise<void>;
  onSaveLookOutfit: (look: FilmCharacterLook, outfit: string) => Promise<void>;
  /** Trả bộ vừa tạo (hoặc bộ trùng tên đã có) để thẻ chọn luôn tab đó. */
  onCreateLook: (c: FilmCharacter, lookKey: string, outfit: string, gen: boolean) => Promise<FilmCharacterLook | null>;
  /** `model` (E3-P5) — ô *Gen lại ▾*; không truyền ⇒ mặc định (Gemini trực tiếp). */
  onGenLook: (look: FilmCharacterLook, model?: string | null) => Promise<unknown>;
  /** E3-P5 — danh mục model gen ảnh (lọc theo cờ đo cho sheet). */
  imageModels?: ImageModelInfo[];
  /** E3-P5 — gen lại các bộ "dựng từ mặt cũ", bộ chính trước. */
  onRegenStale?: (c: FilmCharacter) => void;
  onPickFace: (c: FilmCharacter) => void;
  onGenVoice: (c: FilmCharacter) => void;
  onDelete: (c: FilmCharacter) => void;
  /** Mục riêng cho plan Kling (gen ref / element) — đặt trong menu ⋯. */
  extraMenu?: MenuProps['items'];
  /** Chỗ cắm cuối thẻ cho plan Kling (ô chọn voice_id). */
  footer?: React.ReactNode;
  // ── 260922 E2-P2: trang phục theo cảnh ──
  /** Cảnh của plan đang mở (thứ tự + tên). Rỗng ⇒ ẩn khối "Theo cảnh". */
  scenes: FilmScene[];
  /** Dòng gán cảnh của nhân vật NÀY trong plan đang mở. */
  sceneRows: FilmSceneLook[];
  /** Câu nhắc khi bộ còn được plan khác dùng (null = không nhắc). */
  usageNote: (lookId: string) => string | null;
  /** Đổi bộ cho một đoạn (hoặc một cảnh) — gọi lần lượt trên các dòng. */
  onSetRunLook: (rowIds: string[], lookId: string) => Promise<void>;
  onDeleteLook: (look: FilmCharacterLook) => void;
  // ── 260922 E2-P3: ba chức năng cuối của tab Wardrobe (đã gỡ) — chỉ plan Seedance ──
  /** Gen ảnh MA-NƠ-CANH (đồ không người, `seedanceOutfitRefUrl`) — đường BytePlus (công tắc tay), mục D/B.2. */
  onGenOutfitRef?: (look: FilmCharacterLook) => void;
  /** Xưởng ảnh cho ảnh ma-nơ-canh (owner `LOOK`) — đường BytePlus. */
  onOpenOutfitStudio?: (look: FilmCharacterLook) => void;
  /** Đăng ký / xoá look-asset (`lookAssetAction` quyết) — đường BytePlus. */
  onToggleLookAsset?: (look: FilmCharacterLook) => void;
  // ── 260927 (mục A/B) — ô soạn Custom Prompt + Tải ảnh lên cho ảnh NGƯỜI MẶC ──
  /** Mở ô soạn Custom Prompt (prompt sửa tay + ảnh tham chiếu thêm) cho bộ đang chọn. */
  onOpenSheetComposer?: (c: FilmCharacter, look: FilmCharacterLook) => void;
  /** Tải ảnh lên thay THẲNG ảnh người mặc, không qua AI. */
  onUploadLookRef?: (look: FilmCharacterLook, file: File) => void;
  // ── 260922 E3-P3: sổ asset theo nhà cung cấp (AVIS KYC) ──
  /** Dòng sổ của các bộ đồ của nhân vật NÀY (mọi trạng thái — chip tự tính bằng `refAssetView`). */
  refAssets?: FilmRefAsset[];
  onRegisterAvis?: (look: FilmCharacterLook) => void;
  /** Mở ô đối soát một dòng treo (chip `? AVIS`). */
  onReconcileAvis?: (rowId: string, look: FilmCharacterLook) => void;
  /** 260922 E1-P2 — vai lặp: sửa số người. */
  onSaveHeadcount?: (c: FilmCharacter, n: number | null) => Promise<void>;
  /**
   * 260927 mục N4 — ô "Dùng cho cảnh: …" của composer "Thêm bộ đồ": gán NGAY bộ vừa tạo cho các cảnh được
   * chọn (`film_scene_looks`, APPROVED). Không truyền ⇒ ô chọn ẩn (giữ hành vi cũ nguyên vẹn).
   */
  onAssignScenes?: (lookId: string, sceneIds: string[]) => Promise<unknown>;
  /**
   * 261010 P9D D2 — lưới khung mới: ảnh + chip + nút; «Sửa / Chi tiết» mới bung ngoại hình/giọng/prompt.
   * Không truyền (UI cũ Bank) ⇒ 0-diff thẻ đầy đủ.
   */
  compact?: boolean;
  /**
   * 261010 P10 (S1) — ô «Dựng ảnh từ nhân vật» + dòng «Ảnh dựng từ: …». Cần CẢ HAI: `storyCharacters` (mọi nhân vật của truyện) và `onSaveIdentity`.
   * Không truyền (UI cũ) ⇒ thẻ giống hệt như trước. `onSaveIdentity` trả `true` = đã lưu, `false` = lỗi (form giữ nháp).
   */
  storyCharacters?: FilmCharacter[];
  onSaveIdentity?: (c: FilmCharacter, identity: FilmCharacterIdentityInput | null) => Promise<boolean>;
  /** Số video nhân vật xuất hiện — từ bank-usage của plan (`characterVideoCountOf`); không truyền/null ⇒ chip chỉ có số cảnh. */
  videoCount?: number | null;
}

type EditKey = 'name' | 'role' | 'outfit' | AppearanceKey;

const FilmCharacterCard: React.FC<FilmCharacterCardProps> = (p) => {
  const c = p.character;
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [editing, setEditing] = useState<EditKey | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState<EditKey | null>(null);
  const [adding, setAdding] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newOutfit, setNewOutfit] = useState('');
  // 260927 mục N4 — cảnh chọn để gán NGAY cho bộ sắp tạo. Nguồn = mọi sceneId ĐÃ có dòng gán cho nhân vật
  // này trong plan đang mở (`p.sceneRows` — đúng nghĩa "nhân vật có mặt trong plan", cùng dữ liệu dải
  // "Theo cảnh" đang đọc); nhân vật CHƯA từng qua "chia bộ đồ theo cảnh" thì danh sách rỗng — TD (ghi ở báo cáo).
  const [newSceneIds, setNewSceneIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [creatingDefault, setCreatingDefault] = useState(false);
  const [expandScenes, setExpandScenes] = useState(false);
  const [runBusy, setRunBusy] = useState<string | null>(null);
  const [countOpen, setCountOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const isRole = c.kind === 'ROLE';
  // 261010 P10 (S1): hình thái = nhân vật bình thường + MỘT dòng chữ nhỏ; nhân vật thường ⇒ null (thẻ không đổi).
  const identityLine = p.storyCharacters ? identityLineOf(c, p.storyCharacters) : null;
  const showIdentityField = !!p.storyCharacters && !!p.onSaveIdentity && (c.kind ?? 'PERSON') === 'PERSON';
  const saveCount = async (raw: string) => {
    setCountOpen(false);
    const n = Number(raw);
    const next = Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    if (next !== (c.headcount ?? null)) await p.onSaveHeadcount?.(c, next);
  };

  const vars = useBankVars();

  // E3-P5: thumbnail = mặt ảnh bộ đồ ĐANG dùng (ảnh tải lên / avatar / canonical; gen từ mô tả ⇒ không ảnh).
  const face = faceThumbOf(c);
  const fchip = faceSourceChip(c);
  const fnotes = faceNotes(c);
  const staleCount = staleRegenPlan(p.looks).length;
  const staleOwn = p.looks.filter((l) => l.stale).length;
  const look = pickSelectedLook(p.looks, selectedId);
  const app = (c.appearance || {}) as CharacterAppearance;
  const filled = countFilledLabels(app);
  const gchip = genderChip(c.gender);
  const lookBusy = !!look && p.genLookIds.has(look.id);
  // Thẻ chỉ MỘT ảnh: người mặc bộ đồ; bộ mới chỉ có ma-nơ-canh thì hiện nó kèm nhãn (E2-P3).
  const img = lookImageState(look);
  const fold = characterCardFoldOf(c);
  const folded = !!p.compact && !detailOpen;

  const startEdit = (key: EditKey, value: string) => { setEditing(key); setDraft(value); };

  // ── Theo cảnh (E2-P2) ──
  const usedIn = (lookId: string) => p.sceneRows.filter((r) => r.lookId === lookId).length;
  const allOrders = p.scenes.map((sc) => sc.orderIndex);
  const runs = sceneRuns(p.sceneRows, p.scenes, p.looks);
  // Bung từng cảnh = mỗi dòng một "đoạn" riêng (cùng luật hỏng/khoá).
  const cells: SceneRun[] = expandScenes ? p.sceneRows
    .map((r) => sceneRuns([r], p.scenes, p.looks)[0]).filter(Boolean).sort((a, b) => a.orders[0] - b.orders[0]) : runs;
  const brokenScenes = runs.filter((r) => r.broken).reduce((n, r) => n + r.orders.length, 0);
  const presentScenes = runs.reduce((n, r) => n + r.orders.length, 0);
  const oldest = p.looks.length ? [...p.looks].sort((a, b) => String((a as any).createdAt || '').localeCompare(String((b as any).createdAt || '')))[0] : null;
  const headingOf = (order: number) => p.scenes.find((sc) => sc.orderIndex === order)?.heading || '';
  const setRun = async (run: SceneRun, lookId: string) => {
    const k = run.rowIds.join(',');
    setRunBusy(k);
    try { await p.onSetRunLook(run.rowIds, lookId); } finally { setRunBusy(null); }
  };
  const note = look ? p.usageNote(look.id) : null;
  const cancelEdit = () => { setEditing(null); setDraft(''); };

  /** Lưu dòng đang sửa. Không đổi gì ⇒ đóng, không gọi API. */
  const commit = async () => {
    const key = editing;
    if (!key) return;
    const value = draft.trim();
    setEditing(null);
    try {
      setSaving(key);
      if (key === 'name' || key === 'role') {
        const cur = (key === 'name' ? c.name : c.role) || '';
        if (value === cur.trim() || (key === 'name' && !value)) return;
        await p.onSaveField(c, key, value);
      } else if (key === 'outfit') {
        if (!look || value === (look.outfit || '').trim()) return;
        await p.onSaveLookOutfit(look, value);
      } else {
        if (value === (app[key] || '').trim()) return;
        // Gửi cả 7 nhãn đang có + nhãn vừa sửa: backend thay TRỌN `appearance` và dựng lại `casting`.
        const next = appearancePayload({ ...app, [key]: value }, filled > 0);
        if (next === undefined) return;
        await p.onSaveAppearance(c, next);
      }
    } finally { setSaving(null); }
  };

  const editor = (multiline: boolean, placeholder?: string) => (multiline
    ? <Input.TextArea autoFocus autoSize={{ minRows: 1, maxRows: 5 }} size="small" value={draft} placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit(); if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); } }} />
    : <Input autoFocus size="small" value={draft} placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit} onPressEnter={commit}
        onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit(); }} />);

  const submitLook = async (gen: boolean) => {
    const key = newKey.trim();
    if (!key) return;
    setCreating(true);
    try {
      const made = await p.onCreateLook(c, key, newOutfit.trim(), gen);
      if (made) {
        setSelectedId(made.id);
        // 260927 mục N4 — áp NGAY vào cảnh đã chọn, không đợi lượt "AI gán lại" sau.
        if (newSceneIds.length && p.onAssignScenes) await p.onAssignScenes(made.id, newSceneIds);
      }
      setAdding(false); setNewKey(''); setNewOutfit(''); setNewSceneIds([]);
      return made;
    } finally { setCreating(false); }
  };
  /**
   * 260927 (mục B.4) — "Thêm bộ đồ" kiểu TopView: tạo bộ (không gen) rồi mở NGAY ô soạn Custom Prompt, ảnh 1
   * = sheet bộ chính (khoá) + prompt mồi "Keep the character from image 1 unchanged. Change the outfit
   * to: …" (`renderSheetPrompt` đã dựng đúng nhánh này cho bộ phụ — xem `filmCharacterSheet.ts`).
   */
  const submitLookAndCompose = async () => {
    const made = await submitLook(false);
    if (made && p.onOpenSheetComposer) p.onOpenSheetComposer(c, made);
  };

  /**
   * 260927 mục H — thẻ CHƯA CÓ bộ đồ nào (ví dụ nhân vật phụ vừa `promoteSupportingCast`, chưa qua
   * `ensureEventCharacterLooks`) vẫn phải mở được Custom Prompt: tạo bộ `default` (rỗng, hoặc lấy sẵn
   * `c.wardrobe` nếu mục H đã điền) qua route sẵn có `POST /characters/:id/looks`, rồi mở NGAY ô soạn —
   * không cần người tự gõ tên/mô tả bộ trước như luồng "Thêm bộ đồ" thường.
   */
  const createDefaultAndCompose = async () => {
    setCreatingDefault(true);
    try {
      const made = await p.onCreateLook(c, 'default', c.wardrobe || '', false);
      if (made) { setSelectedId(made.id); if (p.onOpenSheetComposer) p.onOpenSheetComposer(c, made); }
    } finally { setCreatingDefault(false); }
  };

  // E3-P4: bộ phụ dựng từ ảnh bộ chính (`default`, không có thì bộ đầu — `p.looks` đã xếp `default` trước, rồi theo thứ tự server = cũ trước).
  const mainLook = p.looks.find((l) => l.lookKey === 'default') || p.looks[0] || null;
  const isMainSelected = !!look && !!mainLook && look.id === mainLook.id;
  const hint = newLookHint(p.looks, newKey);
  // 260927 mục N4 — cảnh nhân vật này có mặt trong plan (nguồn = `p.sceneRows`, mỗi dòng = một cảnh đã biết
  // nhân vật xuất hiện, bất kể đang gán bộ nào) → tuỳ chọn cho ô "Dùng cho cảnh: …".
  const sceneOptions = Array.from(new Set(p.sceneRows.map((r) => r.sceneId)))
    .map((sceneId) => p.scenes.find((sc) => sc.id === sceneId))
    .filter((sc): sc is FilmScene => !!sc)
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((sc) => ({ value: sc.id, label: `#${sc.orderIndex} ${sc.heading || ''}`.trim() }));
  const modelOptions = look ? sheetModelOptions(p.imageModels || [], look.sheetInput) : [];
  const modelItems: MenuProps['items'] = modelOptions.map((m) => ({ key: m.id, label: m.label }));
  const avisView = look ? refAssetView((p.refAssets || []).filter((r) => r.ownerId === look.id && r.provider === 'AVIS'), look.canonicalRefUrl) : null;
  // 260927 (mục B.2): gỡ "Gen lại ảnh ma-nơ-canh" · "Xưởng ảnh ma-nơ-canh" · "Asset BytePlus (look-asset)" khỏi
  // menu ⋯ của thẻ — chỉ còn AVIS (thứ chặn gen clip ở đường mặc định). Đường BytePlus (công tắc tay) vẫn
  // gọi được qua Xưởng ảnh mở từ tab khác/script — xem TD trong báo cáo Dev.
  const seedanceLookItems: MenuProps['items'] = look && p.isSeedance ? [
    ...(p.onRegisterAvis && look.canonicalRefUrl && avisView && avisView.state !== 'active' && avisView.state !== 'unknown'
      ? [{ key: 'avis', label: avisView.state === 'stale' ? 'Đăng ký AVIS cho ảnh hiện tại' : 'Đăng ký AVIS', onClick: () => p.onRegisterAvis!(look) }] : []),
    { type: 'divider' as const },
  ] : [];
  const menu: MenuProps['items'] = [
    ...seedanceLookItems,
    ...(look ? [{ key: 'del-look', danger: true, label: `Xoá bộ đang chọn (${look.lookKey})`, onClick: () => p.onDeleteLook(look) }] : []),
    ...(p.extraMenu || []),
    ...(p.extraMenu?.length ? [{ type: 'divider' as const }] : []),
    { key: 'delete', danger: true, label: 'Xoá nhân vật',
      onClick: () => Modal.confirm({
        title: `Xoá "${c.name}" khỏi Bank?`, content: 'Các bộ đồ của nhân vật cũng bị xoá.',
        okText: 'Xoá', okButtonProps: { danger: true }, cancelText: 'Huỷ', onOk: () => p.onDelete(c),
      }) },
  ];

  const genderMenu: MenuProps['items'] = [
    { key: 'female', label: '♀ Nữ' }, { key: 'male', label: '♂ Nam' }, { key: 'none', label: 'Bỏ trống' },
  ];

  const midBlocks = (
    <>
      {!folded && look && (
        <div className="bk-outfit">
          Bộ đang chọn ({look.lookKey}):{' '}
          {editing === 'outfit' ? editor(true, 'Mô tả bộ đồ — thứ dùng để gen ảnh')
            : <Tooltip title="Mô tả này là thứ dùng để gen ảnh bộ đồ. Bấm để sửa.">
                <span className="v" onClick={() => startEdit('outfit', look.outfit || '')}>
                  {saving === 'outfit' ? <Spin size="small" /> : (look.outfit || <i>chưa có mô tả — bấm để điền</i>)}
                </span>
              </Tooltip>}
        </div>
      )}

      {!folded && isRole && c.aliases?.length > 0 && <div className="bk-alias">Gồm: {c.aliases.join(', ')}</div>}

      {/* 260927 (mục B.2): gỡ chip kỹ thuật "Người mặc"/"Ma-nơ-canh"/"Asset BytePlus" — chỉ giữ AVIS (thứ chặn
          gen clip ở đường mặc định) + cờ "dựng từ mặt cũ". */}
      {!folded && look && p.isSeedance && (
        <div className="bk-status">
          {look.stale && <span className="bk-chip warn" title={`Ảnh này dựng từ nguồn mặt cũ: ${look.staleReason || 'nguồn mặt đã đổi'}. Gen lại để khớp.`}>{identityLine ? '⚠ Ảnh cũ' : '⚠ Dựng từ mặt cũ'}</span>}
          {look.canonicalRefUrl && avisView && (() => {
            const a = avisChip(avisView);
            return avisView.state === 'unknown' && avisView.rowId && p.onReconcileAvis
              ? <button type="button" className={`bk-chip ${a.cls}`} title={a.title} onClick={() => p.onReconcileAvis!(avisView.rowId!, look)}>{a.text}</button>
              : <span className={`bk-chip ${a.cls}`} title={a.title}>{a.text}</span>;
          })()}
        </div>
      )}

      {look && note && <div className="bk-note">Bộ <b>{look.lookKey}</b> {note}</div>}

      {/* E3-P5 — nguồn mặt + ảnh bộ đồ dựng từ mặt cũ (đóng TD-FILM-101). */}
      {!folded && p.isSeedance && (
        <div className="bk-status">
          <button type="button" className={`bk-chip ${fchip.tone}`} title={`${fchip.title} Bấm để đổi.`} onClick={() => p.onPickFace(c)}>{fchip.text}</button>
        </div>
      )}
      {!folded && p.isSeedance && fnotes.map((n) => <div key={n} className="bk-note">{n}</div>)}
      {!folded && staleOwn > 0 && (
        <div className="bk-note" style={{ borderLeftColor: 'var(--bk-warn)' }}>
          <b>{staleOwn} bộ</b> {identityLine ? 'ảnh cũ (nhân vật gốc hoặc mô tả đã đổi)' : 'dựng từ mặt cũ'}{staleCount > staleOwn ? ` (gen lại bộ chính thì ${staleCount - staleOwn} bộ khác cũng phải gen lại)` : ''}.{' '}
          {p.onRegenStale && <button type="button" className="bk-lnk" onClick={() => p.onRegenStale!(c)}>Gen lại {staleCount} bộ (bộ chính trước)</button>}
        </div>
      )}

      {!folded && p.scenes.length > 0 && (
        <div className="bk-scn">
          <div className="bk-lbl">
            Theo cảnh
            {p.sceneRows.length === 0 ? null
              : <span className={`bk-cnt ${brokenScenes ? 'none' : 'full'}`}>
                  {brokenScenes ? `${brokenScenes} cảnh trỏ bộ đồ đã xoá` : `có mặt ${presentScenes}/${p.scenes.length} cảnh`}
                </span>}
            <span style={{ flex: 1 }} />
            {p.sceneRows.length > 1 && (
              <button type="button" className="bk-lnk" onClick={() => setExpandScenes((v) => !v)}>{expandScenes ? 'Thu gọn' : 'Sửa từng cảnh'}</button>
            )}
          </div>
          {p.sceneRows.length === 0
            ? <div className="bk-dimline">Không xuất hiện trong {p.scenes.length} cảnh của tập này</div>
            : (
              <div className="bk-runs">
                {cells.map((run, idx) => {
                  const l = p.looks.find((x) => x.id === run.lookId);
                  const k = run.rowIds.join(',');
                  const items: MenuProps['items'] = p.looks.map((x) => ({
                    key: x.id,
                    label: <span className="bk-optl">{x.canonicalRefUrl ? <img src={x.canonicalRefUrl} alt="" onLoad={markSheet} /> : <span className="ph" />}{x.lookKey}</span>,
                  }));
                  const title = run.orders.length > 1 ? `Đổi bộ cho cả đoạn ${formatOrders(run.orders, [])}` : `Đổi bộ cho ${formatOrders(run.orders, [])}`;
                  return (
                    <React.Fragment key={k}>
                      {idx > 0 && !expandScenes && <span className="bk-arr">→</span>}
                      <Dropdown trigger={['click']} menu={{ items: [{ key: 'h', label: title, disabled: true }, { type: 'divider' as const }, ...(items || [])], onClick: ({ key }) => { if (key !== 'h') setRun(run, String(key)); } }}>
                        <button type="button" className={`bk-run ${run.broken ? 'bad' : ''} ${run.unassigned ? 'un' : ''}`}>
                          {run.locked && <span className="lock" title="Có cảnh người đã chọn — AI không ghi đè">🔒</span>}
                          {runBusy === k ? <span className="ph"><Spin size="small" /></span>
                            : (!run.broken && l?.canonicalRefUrl ? <img src={l.canonicalRefUrl} alt="" onLoad={markSheet} /> : <span className="ph" />)}
                          <span className="tx">
                            <span className="s">{expandScenes ? formatOrders(run.orders, []) : formatOrders(run.orders, allOrders)}</span>
                            <span className="k">{run.broken ? '⚠ bộ đồ đã bị xoá' : run.unassigned ? 'chưa gán bộ' : (l?.lookKey || '—')}</span>
                            <span className="h">
                              {run.broken ? `đang rơi về: ${oldest?.lookKey || 'bộ cũ nhất'}`
                                : expandScenes ? headingOf(run.orders[0]) : `${run.orders.length} cảnh`}
                            </span>
                          </span>
                        </button>
                      </Dropdown>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
        </div>
      )}

    </>
  );
  const addBlock = (
    <>
      {adding && (
        <div className="bk-inl">
          <div className="hd"><b>Bộ đồ mới cho {c.name}</b></div>
          <Input size="small" placeholder="Tên bộ, vd: torn-tunic · wedding-dress" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
          <Input.TextArea size="small" autoSize={{ minRows: 2, maxRows: 5 }} placeholder="Mô tả bộ đồ từ đầu tới giày + đồ mang theo người"
            value={newOutfit} onChange={(e) => setNewOutfit(e.target.value)} />
          {/* 260927 mục N4 — bộ mới áp vào cảnh NGAY lúc tạo, không đợi lượt "AI gán lại" sau mới thấy đúng bộ. */}
          {p.onAssignScenes && sceneOptions.length > 0 && (
            <Select mode="multiple" allowClear size="small" placeholder="Dùng cho cảnh: … (tuỳ chọn)"
              style={{ width: '100%' }} value={newSceneIds} onChange={setNewSceneIds} options={sceneOptions} />
          )}
          {/* E3-P4 — nói trước ảnh sẽ dựng từ đâu (cùng luật backend `pickSheetFace`). */}
          <div className={`bk-hint ${hint.canGen ? '' : 'warn'}`}>{hint.text}</div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => { setAdding(false); setNewSceneIds([]); }}>Thôi</Button>
            <Button size="small" disabled={!newKey.trim()} loading={creating} onClick={() => submitLook(false)}>Tạo bộ đồ</Button>
            <Tooltip title={!hint.canGen ? hint.text : !newOutfit.trim() ? 'Nhập mô tả bộ đồ trước — không có gì để "đổi đồ theo"' : ''}>
              <Button size="small" type="primary" disabled={!newKey.trim() || !newOutfit.trim() || !hint.canGen || !p.onOpenSheetComposer}
                loading={creating} onClick={submitLookAndCompose}>Tạo và mở Custom Prompt</Button>
            </Tooltip>
          </div>
        </div>
      )}

    </>
  );
  const lowerBlocks = (
    <>
        {!folded && <div className="bk-lbl">Ngoại hình <span className={`bk-cnt ${filled === 7 ? 'full' : filled ? 'part' : 'none'}`}>{filled}/7</span></div>}
        {!folded && <dl className="bk-dl">
          {APPEARANCE_LABELS.map(([key, label]) => {
            const v = (app[key] || '').trim();
            return (
              <React.Fragment key={key}>
                <dt>{label}</dt>
                {editing === key
                  ? <dd style={{ cursor: 'auto', background: 'none' }}>{editor(key === 'facialFeatures' || key === 'clothingProps', APPEARANCE_PLACEHOLDERS[key])}</dd>
                  : <dd className={v ? '' : 'miss'} onClick={() => startEdit(key, v)}>
                      {saving === key ? <Spin size="small" /> : (v || 'Chưa có — bấm để điền')}
                    </dd>}
              </React.Fragment>
            );
          })}
        </dl>}
        {!folded && filled === 0 && c.casting && (
          <div className="bk-old">Mô tả cũ: {c.casting.length > 160 ? `${c.casting.slice(0, 160)}…` : c.casting}</div>
        )}

        {!folded && showIdentityField && <div className="bk-lbl">Dựng ảnh từ nhân vật</div>}
        {!folded && showIdentityField && <CharacterIdentityField character={c} storyCharacters={p.storyCharacters!} onSave={p.onSaveIdentity!} />}

        {!folded && <div className="bk-lbl">Giọng</div>}
        {!folded && <div className="bk-chips">
          <Dropdown trigger={['click']} menu={{ items: genderMenu, onClick: ({ key }) => p.onSaveGender(c, key === 'none' ? null : key) }}>
            <button type="button" className={`bk-chip ${gchip.tone}`}>{gchip.text}</button>
          </Dropdown>
          {p.isSeedance && <span className={`bk-chip ${c.seedanceVoiceRefUrl ? 'ok' : ''}`}>🎙 {c.seedanceVoiceRefUrl ? 'Có giọng mẫu' : 'Chưa có giọng mẫu'}</span>}
        </div>}
        {!folded && p.footer}
    </>
  );

  // ── UI-assettabs: thẻ kiểu bảng (compact). Ảnh trên → tên (1 dòng) + vai → một dòng meta biểu tượng → «Bộ đồ: …» → nút biểu tượng + ⋯. «Sửa / Chi tiết» bung phần cũ. ──
  if (p.compact) {
    const genderView = c.gender === 'female' ? { icon: 'woman' as IconName, tone: 'ok', label: 'Nữ' }
      : c.gender === 'male' ? { icon: 'man' as IconName, tone: 'ok', label: 'Nam' }
        : { icon: 'question' as IconName, tone: 'warn', label: 'Chưa rõ giới — neo giọng V.O.' };
    const avisTone = !avisView ? 'todo' : avisView.state === 'active' ? 'ok' : avisView.state === 'processing' ? 'doing' : avisView.state === 'stale' ? 'warn' : avisView.state === 'none' ? 'todo' : 'bad';
    const avis = avisView ? avisChip(avisView) : null;
    const scenesN = characterCardCountsOf(p.sceneRows).scenes;
    const lookName = look ? lookLabelOf(look.lookKey, c.name) : null;
    const askModel = (m: { id: string; label: string }) => {
      if (!look) return;
      Modal.confirm({
        title: `Tạo ${look.canonicalRefUrl ? 'lại ' : ''}ảnh người mặc bộ "${lookName}" bằng ${m.label}?`,
        content: look.canonicalRefUrl ? 'Ảnh đang dùng sẽ bị thay, không lấy lại được trên UI. Model bị kiểm duyệt thì báo lỗi, KHÔNG tự đổi model.' : 'Model bị kiểm duyệt thì báo lỗi, KHÔNG tự đổi model.',
        okText: 'Tạo', cancelText: 'Thôi', onOk: () => p.onGenLook(look, m.id),
      });
    };
    const regenBtn = look ? (look.canonicalRefUrl ? (
      <Popconfirm title={`Tạo lại ảnh người mặc bộ "${lookName}"?`}
        description={<div style={{ maxWidth: 300 }}>Ảnh đang dùng sẽ bị thay, không lấy lại được trên UI.
          {isMainSelected && p.looks.length > 1 && <><br /><b>Đây là bộ chính</b> — các bộ khác dựng từ ảnh bộ chính và <b>không tự đổi theo</b>; sau khi tạo, thẻ hiện cờ "dựng từ mặt cũ" để tạo lại chúng cho khớp.</>}</div>}
        okText="Tạo lại" cancelText="Thôi" onConfirm={() => p.onGenLook(look)}>
        <span className="wf-ib-w"><WfIconButton icon="reload" label="Tạo lại ảnh người mặc" loading={lookBusy} testId="ac-gen" /></span>
      </Popconfirm>
    ) : <WfIconButton icon="picture" label="Tạo ảnh người mặc" loading={lookBusy} onClick={() => p.onGenLook(look)} testId="ac-gen" />) : null;
    const compactMenu: MenuProps['items'] = [
      ...(p.onOpenSheetComposer ? [look
        ? { key: 'custom', label: 'Custom Prompt…', onClick: () => p.onOpenSheetComposer!(c, look) }
        : { key: 'custom0', label: "Custom Prompt… (tạo bộ 'default' trước)", disabled: creatingDefault, onClick: createDefaultAndCompose }] : []),
      ...(look && modelOptions.length > 0 ? [{ key: 'models', label: 'Tạo bằng model khác (đã đo cho sheet)', disabled: lookBusy,
        children: modelOptions.map((m) => ({ key: m.id, label: m.label, onClick: () => askModel(m) })) }] : []),
      ...(p.isSeedance ? [
        { key: 'face', label: '☺ Chọn mặt', onClick: () => p.onPickFace(c) },
        { key: 'voice', label: '🎙 Tạo giọng mẫu', disabled: p.busyAction === 'voice', onClick: () => p.onGenVoice(c) },
      ] : []),
      { type: 'divider' as const },
      ...(menu || []),
    ];
    const lookSwitch: MenuProps['items'] = p.looks.map((l) => ({ key: l.id, label: lookLabelOf(l.lookKey, c.name), onClick: () => setSelectedId(l.id) }));
    return (
      <div className="ac-card" style={vars} data-testid="ac-char-card">
        <div className="bk-sheet">
          {img.url ? <img className="main" src={img.url} alt={lookName || c.name} />
            : (
              <div className="empty">
                {look ? <>Chưa có ảnh bộ đồ <b>{lookName}</b></> : 'Chưa có bộ đồ nào'}
                {look && <WfIconButton icon="picture" label="Tạo ảnh người mặc" loading={lookBusy} onClick={() => p.onGenLook(look)} />}
              </div>
            )}
          {face
            ? <Tooltip title="Mặt đang ghim — bấm để đổi"><img className="avatar" src={face} alt="" onClick={() => p.onPickFace(c)} /></Tooltip>
            : <Tooltip title="Chưa ghim mặt — bấm để chọn"><button type="button" className="avatar none" onClick={() => p.onPickFace(c)}>☺</button></Tooltip>}
          {img.tag && <span className="bk-tagimg">{img.tag} — chưa có ảnh người mặc</span>}
          {lookBusy && img.url && <div className="empty" style={{ background: 'rgba(0,0,0,.35)' }}><Spin /></div>}
        </div>

        <div className="ac-body">
          {editing === 'name' ? editor(false, 'Tên nhân vật')
            : <h3 className="ac-name" title={c.name} onClick={() => startEdit('name', c.name)}>{saving === 'name' ? <Spin size="small" /> : c.name}</h3>}
          <div className="ac-sub">
            {editing === 'role' ? editor(false, 'Vai, vd: Female lead')
              : <span className={`t${c.role ? '' : ' miss'}`} title={c.role || undefined} onClick={() => startEdit('role', c.role || '')}>{saving === 'role' ? <Spin size="small" /> : (c.role || '＋ vai')}</span>}
            {isRole && <span className="ac-tag">VAI LẶP</span>}
            {isRole && (countOpen
              ? <InputNumber size="small" autoFocus min={1} max={999} defaultValue={c.headcount ?? undefined} style={{ width: 70 }}
                  onBlur={(e) => saveCount((e.target as HTMLInputElement).value)} onPressEnter={(e) => saveCount((e.target as HTMLInputElement).value)} />
              : <span className="t" onClick={() => setCountOpen(true)}>{c.headcount ? `×${c.headcount}` : '＋ số người'}</span>)}
          </div>
          {identityLine ? <div className="bk-dimline" style={{ marginTop: 2 }} data-testid="bk-identity-line">{identityLine}</div> : null}
          <div className="ac-meta" data-testid="bk-card-summary">
            {fold.summary.includes('gender') ? <IconChip label={genderView.label} icon={genderView.icon} tone={genderView.tone} /> : null}
            {fold.summary.includes('kyc') && avis && avisView ? <IconChip label={`AVIS — ${avis.title}`} icon="safety" tone={avisTone} /> : null}
            {typeof p.videoCount === 'number' && p.videoCount > 0 ? <IconChip label={`${p.videoCount} video có nhân vật này`} icon="video" tone="meta" value={String(p.videoCount)} /> : null}
            {fold.summary.includes('counts') ? <IconChip label={`${scenesN} cảnh có nhân vật này`} icon="list" tone="meta" value={String(scenesN)} /> : null}
            {fold.summary.includes('image') ? <IconChip label={img.url ? 'Có ảnh người mặc' : 'Chưa có ảnh người mặc'} icon="picture" tone={img.url ? 'ok' : 'warn'} /> : null}
            {staleOwn > 0 ? <IconChip label={staleChipLabel(staleOwn, !!identityLine)} icon="warning" tone="warn" /> : null}
          </div>
          <div className="ac-look" data-testid="ac-char-look">
            <span>Bộ đồ:</span>
            <span className="v" title={look?.lookKey}>{lookName || 'chưa có'}</span>
            {p.looks.length > 1 ? (
              <Dropdown trigger={['click']} menu={{ items: lookSwitch, selectedKeys: look ? [look.id] : [] }}>
                <span className="wf-ib-w"><WfIconButton icon="down" label={`Đổi bộ đồ (${p.looks.length} bộ)`} /></span>
              </Dropdown>
            ) : null}
            <WfIconButton icon="plus" label="Thêm bộ đồ" pressed={adding} onClick={() => setAdding((v) => !v)} />
          </div>
        </div>

        {addBlock}
        {!detailOpen && note && look ? <div className="ac-note" style={{ margin: '0 12px 6px' }}>Bộ <b>{lookName}</b> {note}</div> : null}
        {detailOpen ? <div className="ac-detail">{midBlocks}{lowerBlocks}</div> : null}

        <div className="ac-acts">
          {regenBtn}
          {look && p.onUploadLookRef ? (
            <Upload accept="image/*" showUploadList={false} customRequest={({ file }) => p.onUploadLookRef!(look, file as File)}>
              <WfIconButton icon="upload" label="Tải ảnh lên" loading={lookBusy} testId="ac-upload" />
            </Upload>
          ) : null}
          <WfIconButton icon="edit" label={detailOpen ? 'Thu gọn' : 'Sửa / Chi tiết'} pressed={detailOpen} onClick={() => setDetailOpen((v) => !v)} testId="ac-detail-toggle" />
          <span className="sp" />
          <Dropdown menu={{ items: compactMenu }} trigger={['click']}>
            <span className="wf-ib-w"><WfIconButton icon="ellipsis" label="Thêm" loading={p.busyAction === 'delete'} testId="ac-more" /></span>
          </Dropdown>
        </div>
      </div>
    );
  }

  return (
    <div className="bk-card" style={vars}>
      {/* ── khung ảnh: MỘT ảnh = ảnh của bộ đồ đang chọn (như TopView). Mặt đang ghim chỉ là thumbnail góc trái. ── */}
      <div className="bk-sheet">
        {img.url ? <img className="main" src={img.url} alt={look?.lookKey} />
          : (
            <div className="empty">
              {look ? <>Chưa có ảnh bộ đồ <b>{look.lookKey}</b></> : 'Chưa có bộ đồ nào'}
              {look && (
                <Button size="small" type="primary" loading={lookBusy} onClick={() => p.onGenLook(look)}>Gen ảnh</Button>
              )}
            </div>
          )}
        {face
          ? <Tooltip title="Mặt đang ghim — bấm để đổi"><img className="avatar" src={face} alt="" onClick={() => p.onPickFace(c)} /></Tooltip>
          : <Tooltip title="Chưa ghim mặt — bấm để chọn"><button type="button" className="avatar none" onClick={() => p.onPickFace(c)}>☺</button></Tooltip>}
        {img.tag && <span className="bk-tagimg">{img.tag} — chưa có ảnh người mặc</span>}
        {lookBusy && img.url && <div className="empty" style={{ background: 'rgba(0,0,0,.35)' }}><Spin /></div>}
      </div>

      {/* ── tab bộ đồ ── */}
      <div className="bk-looks">
        {p.looks.map((l) => (
          <button key={l.id} type="button" className={`bk-look ${look?.id === l.id ? 'on' : ''}`} onClick={() => setSelectedId(l.id)}>
            {l.lookKey === 'default' && <span className="main">★ Chính</span>}
            {lookImageState(l).url ? <img src={lookImageState(l).url!} alt="" onLoad={markSheet} /> : <span className="ph" />}
            <span className="t">{c.name}·{l.lookKey}</span>
            {l.stale && <span className="n" title={`Dựng từ mặt cũ — ${l.staleReason || ''}`} style={{ color: 'var(--bk-warn)' }}>⚠</span>}
            {usedIn(l.id) > 0 && <span className="n">· {usedIn(l.id)} cảnh</span>}
          </button>
        ))}
        <button type="button" className="bk-look add" onClick={() => setAdding((v) => !v)}>＋ Thêm bộ đồ</button>
      </div>

      {midBlocks}
      {!folded && addBlock}
      {/* ── hàng nút ── */}
      <div className="bk-acts">
        {look && (
          look.canonicalRefUrl
            ? (
              <Popconfirm title={`Gen lại ảnh người mặc bộ "${look.lookKey}"?`}
                description={<div style={{ maxWidth: 300 }}>Ảnh đang dùng sẽ bị thay, không lấy lại được trên UI.
                  {isMainSelected && p.looks.length > 1 && <><br /><b>Đây là bộ chính</b> — các bộ khác dựng từ ảnh bộ chính và <b>không tự đổi theo</b>; sau khi gen, thẻ hiện cờ "dựng từ mặt cũ" để gen lại chúng cho khớp.</>}</div>}
                okText="Gen lại" cancelText="Thôi"
                onConfirm={() => p.onGenLook(look)}>
                <Button size="small" type="primary" loading={lookBusy}>Gen lại ảnh người mặc</Button>
              </Popconfirm>
            )
            : <Button size="small" type="primary" loading={lookBusy} onClick={() => p.onGenLook(look)}>Gen ảnh người mặc</Button>
        )}
        {/* E3-P5 — ô chọn model: chỉ model đã ĐO vẽ được sheet cho đúng loại ảnh vào của bộ này (server trả `sheetInput`). Không giá. */}
        {look && modelItems.length > 0 && (
          <Dropdown trigger={['click']} disabled={lookBusy} menu={{
            items: [{ key: 'h', label: 'Gen bằng model khác (đã đo cho sheet)', disabled: true }, { type: 'divider' as const }, ...modelItems],
            onClick: ({ key }) => {
              if (key === 'h') return;
              const m = modelOptions.find((x) => x.id === key);
              Modal.confirm({
                title: `Gen ${look.canonicalRefUrl ? 'lại ' : ''}ảnh người mặc bộ "${look.lookKey}" bằng ${m?.label || key}?`,
                content: look.canonicalRefUrl ? 'Ảnh đang dùng sẽ bị thay, không lấy lại được trên UI. Model bị kiểm duyệt thì báo lỗi, KHÔNG tự đổi model.' : 'Model bị kiểm duyệt thì báo lỗi, KHÔNG tự đổi model.',
                okText: 'Gen', cancelText: 'Thôi', onOk: () => p.onGenLook(look, key),
              });
            },
          }}>
            <Tooltip title="Gen bằng model khác — chỉ model đã đo vẽ được sheet"><Button size="small" loading={lookBusy}>▾</Button></Tooltip>
          </Dropdown>
        )}
        {/* 260927 (mục B.1) — "Tải ảnh lên" thay THẲNG, "Custom Prompt" mở ô soạn (mục A). */}
        {look && p.onUploadLookRef && (
          <Upload accept="image/*" showUploadList={false} customRequest={({ file }) => p.onUploadLookRef!(look, file as File)}>
            <Button size="small" loading={lookBusy}>Tải ảnh lên</Button>
          </Upload>
        )}
        {!folded && look && p.onOpenSheetComposer && (
          <Button size="small" onClick={() => p.onOpenSheetComposer!(c, look)}>Custom Prompt</Button>
        )}
        {/* 260927 mục H — chưa có bộ đồ nào (nhân vật phụ mới promote chưa qua ensure-looks): vẫn cho mở
            Custom Prompt ngay, tự tạo bộ 'default' trước khi mở ô soạn. */}
        {!folded && !look && p.onOpenSheetComposer && (
          <Tooltip title="Tạo bộ đồ 'default' rồi mở ô soạn Custom Prompt cho ảnh người mặc">
            <Button size="small" type="primary" loading={creatingDefault} onClick={createDefaultAndCompose}>Custom Prompt</Button>
          </Tooltip>
        )}
        {!folded && p.isSeedance && <Button size="small" onClick={() => p.onPickFace(c)} loading={p.busyAction === 'seedance'}>☺ Chọn mặt</Button>}
        {!folded && p.isSeedance && <Button size="small" onClick={() => p.onGenVoice(c)} loading={p.busyAction === 'voice'}>🎙 Giọng</Button>}
      </div>

      {/* ── thân thẻ ── */}
      <div className="bk-body">
        <div className="bk-name">
          {editing === 'name' ? editor(false, 'Tên nhân vật')
            : <h3 onClick={() => startEdit('name', c.name)}>{saving === 'name' ? <Spin size="small" /> : c.name}</h3>}
          {editing === 'role' ? editor(false, 'Vai, vd: Female lead')
            : <span className="bk-role" onClick={() => startEdit('role', c.role || '')}>{saving === 'role' ? <Spin size="small" /> : (c.role || '＋ vai')}</span>}
          {/* 260922 E1-P2 — vai lặp: MỘT hồ sơ cho "Guard 1", "Guard 2"; số người sửa được. */}
          {isRole && <span className="bk-kind">VAI LẶP</span>}
          {isRole && (countOpen
            ? <InputNumber size="small" autoFocus min={1} max={999} defaultValue={c.headcount ?? undefined} style={{ width: 70 }}
                onBlur={(e) => saveCount((e.target as HTMLInputElement).value)} onPressEnter={(e) => saveCount((e.target as HTMLInputElement).value)} />
            : <span className="bk-role" onClick={() => setCountOpen(true)}>{c.headcount ? `×${c.headcount}` : '＋ số người'}</span>)}
          <Dropdown menu={{ items: menu }} trigger={['click']}>
            <Button className="more" size="small" type="text" icon={<MoreOutlined />} loading={p.busyAction === 'delete'} />
          </Dropdown>
        </div>
        {identityLine ? <div className="bk-dimline" style={{ marginTop: 2 }} data-testid="bk-identity-line">{identityLine}</div> : null}

      {lowerBlocks}
      </div>
    </div>
  );
};

export default FilmCharacterCard;
